import { gql, QueryResult, useQuery } from '@apollo/client';
import React, { createContext, useContext, useEffect } from 'react';
import { AuthenticatedUserQuery } from '@pv/common/graphql/operations';
import { usePostHog } from 'posthog-js/react';

type User = AuthenticatedUserQuery['authenticatedUser'];

const atlasEnabled = process.env.REACT_APP_PV_ENV === 'production';

const userQuery = gql`
  query AuthenticatedUser {
    authenticatedUser {
      id
      publicId
      atlasHash
      phone
      email
      firstName
      lastName
      fullName
      image
      signatureBlock
      connectedGoogleCalendar
      showLeadsOnGoogleCalendar
      showProposalSentOnGoogleCalendar
      createdAt
      role
      suggestMessages
      schedulingLink
      merriConnected
      prismmConnected
      sevenShiftsConnected
      eventbriteConnected
      cventConnected
      organizationId
      organization {
        id
        publicId
        canUpdate {
          value
        }
        canManageBilling {
          value
        }
        canCancelEvents {
          value
        }
        canManageExpressBookRequests {
          value
        }
        name
        billingEmail
        stripeCustomerId
        freeTrialEndsAt
        image
        banner
        contactFormBackgroundColor
        helloLeadFormPath
        helloLeadFormUrl
        featureFlags
        slug
        calendarWeekStart
        useSubtotalForReporting
      }
      organizationMembership {
        id
        role
        canUpdate {
          value
        }
        canDestroy {
          value
        }
        newLeadsEmail
        newLeadsSms
        paymentEmails
        teamMessageEmail
        weeklyDigestEmail
        reviewSubmittedEmail
        ownerAssignedEmail
        initialMember
      }
      activeVenueMemberships {
        id
        venue {
          id
          publicId
          name
          internalName
          merriId
          sevenShiftsId
          prismmId
          venueCollection {
            id
            name
          }
        }
      }
    }
  }
`;

interface AuthContext {
  user: User | null;
}

interface UserContext extends AuthContext {
  freeTrialEndsAt: Date | null;
  refetchAuthenticatedUser: QueryResult['refetch'];
}
const AuthenticatedUserContext = createContext<UserContext>({
  user: null,
  freeTrialEndsAt: null,
  refetchAuthenticatedUser: () => false as any,
});
export const useUserAuth = () => useContext(AuthenticatedUserContext);

interface ProviderProps {
  children: React.ReactNode;
}

export const AuthUserProvider = ({ children }: ProviderProps) => {
  const posthog = usePostHog();
  const { data, refetch, loading } =
    useQuery<AuthenticatedUserQuery>(userQuery);

  const user = data?.authenticatedUser;

  useEffect(() => {
    if (atlasEnabled && window.Atlas && user) {
      window.Atlas.call('identify', {
        userId: user.publicId,
        name: user.fullName,
        email: user.email,
        userHash: user.atlasHash,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      if (posthog.get_distinct_id() !== user.publicId) {
        // Reset when PostHog has a stale identity from a previous user.
        // This happens when an admin uses "log in as user" which bypasses normal logout.
        // resetDeviceId=true prevents cross-user aliasing via shared $device_id.
        posthog.reset(true);
      }
      posthog.identify(user.publicId, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      });
    } else if (!loading) {
      posthog.reset();
    }
  }, [user, posthog, loading]);

  useEffect(() => {
    if (user?.organization) {
      posthog.group('organization', user.organization.publicId, {
        name: user.organization.name,
      });
    }
  }, [user, posthog]);

  const organization = user?.organization;

  const freeTrialEndsAt = organization?.freeTrialEndsAt;
  const value = {
    user,
    freeTrialEndsAt,
    refetchAuthenticatedUser: refetch,
  };

  return (
    <AuthenticatedUserContext.Provider value={value}>
      {loading ? <></> : children}
    </AuthenticatedUserContext.Provider>
  );
};
